import type {
  BillingInterval,
  BillingPlanCatalogItem,
  BillingPlanKey,
  BillingState,
  EntitlementOverage,
} from '@dukanti/shared';
import { BILLING_STATE_TRANSLATION_KEYS, type TranslationKey } from '@dukanti/shared';

export function formatBillingPrice(
  amountAgorot: number,
  locale: string,
  currency: 'ILS' = 'ILS',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amountAgorot / 100);
}

export function formatBillingDate(value: string | null, locale: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

export function formatBytes(bytes: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: bytes >= 1_000_000_000 ? 'gigabyte' : 'megabyte',
    maximumFractionDigits: 1,
  }).format(bytes / (bytes >= 1_000_000_000 ? 1_000_000_000 : 1_000_000));
}

export function planNameKey(planKey: BillingPlanKey): TranslationKey {
  return `admin.billing.plan.${planKey}.name` as TranslationKey;
}

export function stateKey(state: BillingState): TranslationKey {
  return BILLING_STATE_TRANSLATION_KEYS[state];
}

export function intervalPriceKey(interval: BillingInterval): TranslationKey {
  return interval === 'annual'
    ? 'admin.billing.plan.priceAnnual'
    : 'admin.billing.plan.priceMonthly';
}

export function findPlan(
  catalog: readonly BillingPlanCatalogItem[],
  planKey: BillingPlanKey,
  interval: BillingInterval,
): BillingPlanCatalogItem | null {
  return catalog.find((plan) => plan.planKey === planKey && plan.interval === interval) ?? null;
}

export function overageLabelKey(overage: EntitlementOverage): TranslationKey {
  if (overage.gate === 'active_products') return 'admin.billing.warning.overProductLimit';
  if (overage.gate === 'media_storage') return 'admin.billing.warning.overStorageLimit';
  return 'admin.billing.warning.overAiImageEdits';
}
